/**
 * Carries a device login request across a sign-in in this tab's `sessionStorage`.
 * It survives the GitHub and GitLab round trip, and no other tab can resume it.
 */
import { parseDeviceLoginRequestId } from './device-login.util';

const CARRY_KEY = 'deviceLogin';

/** The BE lifetime of a request, so an older one has expired anyway. */
const CARRY_MAX_AGE_MS = 5 * 60 * 1000;

export const rememberDeviceLoginRequest = (requestId: string): void =>
  sessionStorage.setItem(CARRY_KEY, JSON.stringify({ at: Date.now(), requestId }));

export const forgetDeviceLoginRequest = (): void => sessionStorage.removeItem(CARRY_KEY);

export const readCarriedDeviceLoginRequest = (): string | null => {
  try {
    const { at, requestId } = JSON.parse(sessionStorage.getItem(CARRY_KEY) ?? 'null');
    return Date.now() - at < CARRY_MAX_AGE_MS ? parseDeviceLoginRequestId(requestId) : null;
  } catch (e) {
    return null;
  }
};

/**
 * Where a sign-in lands while a device login waits: the first active organization, with no picker and no dashboard.
 * `onFullyAuthedResume$` navigates back to `/authorize-login` once it is stored. Null means the sign-in lands as usual.
 */
export const deviceLoginLanding = (activeClientUsers: Array<{ id: string; }>): string | null =>
  readCarriedDeviceLoginRequest() ? activeClientUsers[0]?.id ?? null : null;
